import { useEffect, useState } from "react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { PageHeader, Card, Field, inputClass, EmptyState } from "../components/common";
import { CameraCapture } from "../components/CameraCapture";
import { toast } from "sonner";
import { SignIn, SignOut, CheckCircle, XCircle, ShieldWarning, Clock } from "@phosphor-icons/react";
export default function Access() {
  const [employees, setEmployees] = useState([]);
  const [logs, setLogs] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [tipo, setTipo] = useState("entrada");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [summaryDate, setSummaryDate] = useState(new Date().toISOString().slice(0, 10));
  const [summaryEnd, setSummaryEnd] = useState("");
  const [summary, setSummary] = useState(null);
  const [exporting, setExporting] = useState(false);
  const getSummary = async () => {
    try {
      const q = `date=${summaryDate}${summaryEnd ? `&end=${summaryEnd}` : ""}`;
      const { data } = await api.get(`/access/summary?${q}`); setSummary(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const exportSummaryExcel = async () => {
    setExporting(true);
    try {
      const q = `date=${summaryDate}${summaryEnd ? `&end=${summaryEnd}` : ""}`;
      const r = await api.get(`/access/summary/excel?${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `accesos_${summaryDate}${summaryEnd ? `_${summaryEnd}` : ""}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setExporting(false); }
  };

  const loadLogs = () => api.get("/access").then((r) => setLogs(r.data));
  useEffect(() => { api.get("/employees").then((r) => setEmployees(r.data)); loadLogs(); }, []);

  const emp = employees.find((e) => e.id === employeeId);

  const submit = async (e) => {
    e.preventDefault();
    if (!employeeId) return toast.error("Selecciona un empleado");
    if (!file) return toast.error("Toma o sube una foto");
    setBusy(true);
    const fd = new FormData();
    fd.append("employee_id", employeeId);
    fd.append("tipo", tipo);
    fd.append("photo", file);
    try {
      const { data } = await api.post("/access", fd);
      setResult(data);
      if (data.authorized) toast.success(`Acceso AUTORIZADO · ${tipo}`);
      else toast.error("Acceso DENEGADO · falta documentación");
      if (data.identity && data.identity.match === false) toast.error("⚠ La identidad no coincide con el registro del empleado");
      setFile(null); loadLogs();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader overline="Checador de obra" title="Control de acceso" />

      <Card className="p-4 mb-6" data-testid="workforce-summary">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Desde (día)"><input type="date" data-testid="summary-date" className={inputClass} value={summaryDate} onChange={(e) => setSummaryDate(e.target.value)} /></Field>
          <Field label="Hasta (opcional, para rango)"><input type="date" data-testid="summary-end" className={inputClass} value={summaryEnd} onChange={(e) => setSummaryEnd(e.target.value)} /></Field>
          <button onClick={getSummary} data-testid="get-summary-btn" className="h-11 px-4 bg-primary text-white rounded-sm font-semibold text-sm hover:bg-orange-600 transition-colors">Obtener resumen</button>
          {summary && <button onClick={exportSummaryExcel} disabled={exporting} data-testid="export-summary-btn" className="h-11 px-4 border border-input rounded-sm font-semibold text-sm hover:bg-muted transition-colors disabled:opacity-60">{exporting ? "Exportando…" : "Exportar a Excel"}</button>}
        </div>
        {summary && (
          <div className="mt-4" data-testid="summary-result">
            <div className="text-sm font-semibold">{summary.total} persona(s) única(s) entraron a laborar {summary.range ? `del ${summary.start} al ${summary.end}` : `el ${summary.date}`}</div>
            {summary.range && Object.keys(summary.by_day || {}).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="summary-by-day">{Object.entries(summary.by_day).map(([d, n]) => <span key={d} className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 font-semibold">{d}: {n}</span>)}</div>
            )}
            {Object.keys(summary.by_contractor || {}).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">{Object.entries(summary.by_contractor).map(([c, n]) => <span key={c} className="px-2 py-0.5 rounded-full text-xs bg-muted">{c}: {n}</span>)}</div>
            )}
            <div className="mt-3 divide-y divide-border max-h-64 overflow-y-auto">
              {summary.workers.map((w, i) => (
                <div key={i} className="py-1.5 text-sm flex justify-between gap-3"><span>{w.employee_name} <span className="text-muted-foreground">· {w.contractor_name}</span></span><span className="text-xs text-muted-foreground">{summary.range ? `${w.days_present} día(s)` : new Date(w.first_entry).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</span></div>
              ))}
            </div>
          </div>
        )}
      </Card>


      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* register panel */}
        <Card className="lg:col-span-2 p-6 h-fit">
          <div className="overline mb-4">Registrar entrada / salida</div>
          <form onSubmit={submit} className="space-y-4">
            <Field label="Empleado *">
              <select data-testid="access-employee" required className={inputClass} value={employeeId}
                onChange={(e) => { setEmployeeId(e.target.value); setResult(null); }}>
                <option value="">Selecciona…</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>{e.name}{e.authorized ? "" : " (sin papelería)"}</option>
                ))}
              </select>
            </Field>

            {emp && (
              <div className={`rounded-sm p-3 text-sm ${emp.authorized ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`}>
                <div className="font-semibold">{emp.company || emp.contractor_name || "Empresa no asignada"}</div>
                {emp.authorized ? (
                  <div className="inline-flex items-center gap-1 mt-1"><CheckCircle size={15} weight="fill" /> Empleado autorizado</div>
                ) : (
                  <div className="mt-1">
                    <div className="inline-flex items-center gap-1 font-semibold"><ShieldWarning size={15} weight="fill" /> No autorizado — falta:</div>
                    <ul className="list-disc pl-5 mt-0.5">{(emp.missing_docs || []).map((m, i) => <li key={i}>{m}</li>)}</ul>
                  </div>
                )}
              </div>
            )}

            <Field label="Movimiento *">
              <div className="grid grid-cols-2 gap-2">
                {[{ v: "entrada", l: "Entrada", I: SignIn }, { v: "salida", l: "Salida", I: SignOut }].map((o) => (
                  <button key={o.v} type="button" onClick={() => setTipo(o.v)} data-testid={`tipo-${o.v}`}
                    className={`h-11 rounded-sm font-semibold inline-flex items-center justify-center gap-2 border transition-colors duration-200 ${
                      tipo === o.v ? "bg-primary text-white border-primary" : "bg-white border-input text-muted-foreground hover:bg-muted"}`}>
                    <o.I size={18} weight="bold" /> {o.l}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Foto del empleado *"><CameraCapture onCapture={setFile} /></Field>

            <button type="submit" disabled={busy} data-testid="register-access-btn"
              className="w-full h-11 bg-primary text-white font-semibold rounded-sm hover:bg-orange-600 transition-colors duration-200 disabled:opacity-60">
              {busy ? "Registrando…" : "Registrar acceso"}
            </button>
          </form>

          {result && (
            <>
              <div className={`mt-4 rounded-sm p-4 text-center ${result.authorized ? "bg-emerald-600" : "bg-danger"} text-white`} data-testid="access-result">
                {result.authorized
                  ? <><CheckCircle size={28} weight="fill" className="mx-auto" /><div className="font-heading font-bold text-lg mt-1">ACCESO AUTORIZADO</div></>
                  : <><XCircle size={28} weight="fill" className="mx-auto" /><div className="font-heading font-bold text-lg mt-1">ACCESO DENEGADO</div><div className="text-sm opacity-90 mt-1">Complete la papelería del empleado</div></>}
              </div>
              {result.identity && (
                <div className={`mt-3 rounded-sm p-3 text-sm ${result.identity.match === true ? "bg-emerald-50 text-emerald-800" : result.identity.match === false ? "bg-red-50 text-red-800" : "bg-amber-50 text-amber-800"}`} data-testid="identity-result">
                  <div className="font-semibold">{result.identity.match === true ? "Identidad verificada por IA ✓" : result.identity.match === false ? "⚠ La identidad NO coincide" : "Identidad no verificada"}</div>
                  <div className="text-xs mt-0.5">{result.identity.motivo}{typeof result.identity.confianza === "number" ? ` · confianza ${result.identity.confianza}%` : ""}</div>
                </div>
              )}
            </>
          )}
        </Card>

        {/* logs */}
        <div className="lg:col-span-3">
          <div className="overline mb-3">Registros recientes</div>
          {logs.length === 0 ? (
            <EmptyState icon={Clock} title="Sin registros" subtitle="Los accesos registrados aparecerán aquí." />
          ) : (
            <div className="space-y-3">
              {logs.map((l) => (
                <Card key={l.id} data-testid={`access-log-${l.id}`} className="p-4 flex items-center gap-4">
                  <img src={fileUrl(l.photo_path)} alt={l.employee_name} className="w-14 h-14 rounded-sm object-cover border border-border" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{l.employee_name}</div>
                    <div className="text-sm text-muted-foreground truncate">{l.contractor_name}</div>
                    <div className="text-xs text-muted-foreground">{new Date(l.timestamp).toLocaleString("es-MX")}</div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-semibold capitalize ${l.tipo === "entrada" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-700"}`}>
                      {l.tipo === "entrada" ? <SignIn size={13} weight="bold" /> : <SignOut size={13} weight="bold" />} {l.tipo}
                    </span>
                    <div className={`mt-1 inline-flex items-center gap-1 text-xs font-semibold ${l.authorized ? "text-success" : "text-danger"}`}>
                      {l.authorized ? <><CheckCircle size={13} weight="fill" /> Autorizado</> : <><XCircle size={13} weight="fill" /> Denegado</>}
                    </div>
                    {l.identity && l.identity.match === false && <div className="mt-0.5 text-xs font-semibold text-amber-600">Identidad no coincide</div>}
                    {l.identity && l.identity.match === true && <div className="mt-0.5 text-xs font-semibold text-emerald-600">Identidad ✓</div>}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
